import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Building, CheckCircle, FileText, Loader2, Mail, Phone, ReceiptText, Search, UserCog, Wallet,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INVOICED_STATUSES = ['invoiced', 'paid', 'partially_paid', 'overdue', 'facture', 'payee'];

export const isOperationInvoiced = (operation) => Boolean(
  operation?.invoiceNumber
  || operation?.invoice?.invoiceNumber
  || INVOICED_STATUSES.includes(operation?.financialStatus)
);

const fmt = (n) => (n || n === 0
  ? `${Number(n).toLocaleString('fr-FR')} FCFA`
  : '—');

const ImpactRecap = ({ impact }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3 space-y-2" data-testid="reassign-impact-recap">
    <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">
      Ce qui sera modifié
    </p>
    <div className="flex items-start gap-2 text-xs text-gray-700" data-testid="reassign-impact-quote">
      <FileText className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0" />
      <span>
        Devis {impact.quote?.quoteNumber || '(non émis)'} — suivra le nouveau client
      </span>
    </div>
    <div className="flex items-start gap-2 text-xs text-gray-700" data-testid="reassign-impact-invoice">
      <ReceiptText className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0" />
      {impact.invoice?.willBeRegenerated ? (
        <span>
          Facture <strong>{impact.invoice.invoiceNumber}</strong> ({fmt(impact.invoice.amount)}) sera
          <strong className="text-amber-700"> annulée</strong> et une <strong>nouvelle facture</strong> émise
          au nom du nouveau client — montants inchangés
        </span>
      ) : (
        <span>Aucune facture émise</span>
      )}
    </div>
    <div className="flex items-start gap-2 text-xs text-gray-700" data-testid="reassign-impact-payments">
      <Wallet className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0" />
      <span>
        {impact.payments?.count || impact.payments?.advancesCount
          ? `${impact.payments.count} paiement(s) · ${impact.payments.advancesCount} acompte(s) — encaissé ${fmt(impact.payments.amountPaid)} : suit l'opération`
          : 'Aucun paiement ni acompte enregistré'}
      </span>
    </div>
    {impact.externalRef?.current && (
      <div className="flex items-start gap-2 text-xs text-gray-700" data-testid="reassign-impact-extref">
        <Building className="h-3.5 w-3.5 mt-0.5 text-gray-400 shrink-0" />
        <span>
          Référence externe « {impact.externalRef.current} » : conservée uniquement si la nouvelle
          société cliente l'utilise
        </span>
      </div>
    )}
  </div>
);

/**
 * Réaffecte une opération à un autre compte client, quel que soit son état.
 * Facture émise → ancienne annulée + nouvelle facture. Paiements → motif obligatoire.
 */
export default function ReassignClientDialog({ open, onClose, operation, onSaved }) {
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);

  const fetchClients = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/unified-users/clients/search`, {
        params: { q, limit: 30 },
      });
      setClients(data.clients || []);
    } catch (err) {
      toast({
        title: 'Recherche impossible',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchClients(search), 300);
    return () => clearTimeout(t);
  }, [open, search, fetchClients]);

  useEffect(() => {
    if (!open || !operation?.id) return;
    axios.get(`${API}/admin/operations/${operation.id}/reassign-impact`)
      .then(({ data }) => setImpact(data))
      .catch(() => setImpact(null));
  }, [open, operation?.id]);

  useEffect(() => {
    if (!open) {
      setSearch(''); setSelected(null); setReason(''); setImpact(null);
    }
  }, [open]);

  const reasonRequired = Boolean(impact?.requiresReason);
  const reasonMissing = reasonRequired && !reason.trim();

  const handleSave = async () => {
    if (!selected || reasonMissing) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/operations/${operation.id}/client`,
        { clientId: selected.id, reason: reason.trim() },
      );
      toast({ title: 'Client réaffecté', description: data?.message });
      onSaved?.();
    } catch (err) {
      toast({
        title: 'Réaffectation impossible',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const currentLabel = `${operation?.firstName || ''} ${operation?.lastName || ''}`.trim()
    || operation?.email || '—';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] sm:w-full sm:max-w-lg max-h-[92vh] overflow-y-auto grid-cols-[minmax(0,1fr)] [&>*]:min-w-0"
        data-testid="reassign-client-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5 text-emerald-600" />
            Changer le client de l'opération
          </DialogTitle>
          <DialogDescription>
            Rattachement actuel : <strong>{currentLabel}</strong>
            {operation?.companyName ? ` — ${operation.companyName}` : ''}. La modification est
            tracée dans le journal d'audit.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {impact && <ImpactRecap impact={impact} />}

          {reasonRequired && (
            <Alert className="border-amber-300 bg-amber-50" data-testid="reassign-reason-required-alert">
              <Wallet className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs">
                Des paiements ou acomptes sont enregistrés : le motif est <strong>obligatoire</strong>.
                Les encaissements restent attachés à l'opération et suivront le nouveau client.
              </AlertDescription>
            </Alert>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, email, téléphone, entreprise..."
              className="pl-10"
              data-testid="reassign-client-search"
            />
          </div>

          <div className="max-h-[240px] overflow-y-auto space-y-1.5" data-testid="reassign-client-list">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              </div>
            ) : clients.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">Aucun client trouvé</p>
            ) : clients.map((c) => (
              <button
                type="button"
                key={c.id}
                onClick={() => setSelected(c)}
                data-testid={`reassign-client-option-${c.id}`}
                className={`w-full text-left p-2.5 rounded-lg border transition-colors ${
                  selected?.id === c.id
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.firstName} {c.lastName}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-2 truncate min-w-0">
                      <Mail className="h-3 w-3 shrink-0" /><span className="truncate">{c.email}</span>
                      {c.phone && (<><Phone className="h-3 w-3 shrink-0" /><span className="truncate">{c.phone}</span></>)}
                    </p>
                    {c.companyName && (
                      <p className="text-[11px] text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                        <Building className="h-3 w-3 shrink-0" />{c.companyName}
                      </p>
                    )}
                  </div>
                  {selected?.id === c.id && <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />}
                </div>
              </button>
            ))}
          </div>

          <div>
            <Label htmlFor="reassign-reason" className="text-sm">
              Motif {reasonRequired ? <span className="text-red-600">*</span> : '(optionnel)'}
            </Label>
            <Input
              id="reassign-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ex: opération créée par erreur sur le compte admin"
              className={reasonMissing ? 'border-red-300 focus-visible:ring-red-400' : ''}
              data-testid="reassign-client-reason"
            />
            {reasonMissing && (
              <p className="text-[11px] text-red-600 mt-1" data-testid="reassign-reason-error">
                Motif obligatoire (paiements enregistrés)
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !selected || reasonMissing}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="reassign-client-confirm"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCog className="h-4 w-4 mr-2" />}
            Réaffecter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
